import {ManifestStop} from "../../../../generated/graphql";
import {DateTimeErrorMsg} from "./CompleteStopDialog";

const getStopsForJobSortedByManifestSequence = (stops: ManifestStop[], jobId: number) => {
  return stops.filter((stop) => stop.job.jobId === jobId).sort((a, b) => a.manifestSequence - b.manifestSequence);
};

const isDateTimeOutsideTimeWindowOfOtherStopsInSameJob = (
  dateTime: string | undefined,
  stops: ManifestStop[],
  jobId: number,
  errorMessage: string
) => {
  if (!dateTime || !stops) {
    return true;
  }

  const dateTimeMs = +new Date(dateTime);
  //filter stops to provided jobId ordered by manifestSequence
  const stopsForJob = getStopsForJobSortedByManifestSequence(stops, jobId);

  for (const stop of stopsForJob) {
    if (stop.arriveDateTime && stop.departDateTime && stop.manifestSequence !== 0) {
      // Skip the first stop
      const arriveDateTimeMs = +new Date(stop.arriveDateTime);
      const departDateTimeMs = +new Date(stop.departDateTime);

      if (dateTimeMs >= arriveDateTimeMs && dateTimeMs <= departDateTimeMs) {
        return errorMessage;
      }
    }
  }

  return true;
};

const isDateTimeAfterScheduledTimeOfStop2 = (
  dateTime: string | undefined,
  stops: ManifestStop[] | undefined,
  jobId: number,
  errorMessage: string
) => {
  //filter stops to provided jobId ordered by manifestSequence and select second element in array
  const scheduledDateTimeOfStop2 = getStopsForJobSortedByManifestSequence(stops ?? [], jobId)[1]?.scheduledDateTime;

  if (scheduledDateTimeOfStop2 && dateTime) {
    const dateTimeValid = +new Date(dateTime) >= +new Date(scheduledDateTimeOfStop2);
    if (dateTimeValid) {
      return true;
    }
  }
  return errorMessage;
};

const CompleteStopIdexxRules = {
  isPodDateTimeAfterScheduledTimeOfStop2: (
    podDateTime: string | undefined,
    stops: ManifestStop[] | undefined,
    jobId: number
  ) => {
    return isDateTimeAfterScheduledTimeOfStop2(podDateTime, stops, jobId, DateTimeErrorMsg.PODAfterScheduledTime);
  },

  isArrivalDateTimeAfterScheduledTimeOfStop2: (
    arrivalDateTime: string | undefined,
    stops: ManifestStop[] | undefined,
    jobId: number
  ) => {
    return isDateTimeAfterScheduledTimeOfStop2(
      arrivalDateTime,
      stops,
      jobId,
      DateTimeErrorMsg.ArrivalAfterScheduledTime
    );
  },

  isDepartureDateTimeAfterScheduledTimeOfStop2: (
    departureDateTime: string | undefined,
    stops: ManifestStop[] | undefined,
    jobId: number
  ) => {
    return isDateTimeAfterScheduledTimeOfStop2(
      departureDateTime,
      stops,
      jobId,
      DateTimeErrorMsg.DepartureAfterScheduledTime
    );
  },

  isPodDateTimeOutsideTimeWindowOfOtherStopsInSameJob: (
    podDateTime: string | undefined,
    stops: ManifestStop[],
    jobId: number
  ) => {
    return isDateTimeOutsideTimeWindowOfOtherStopsInSameJob(
      podDateTime,
      stops,
      jobId,
      DateTimeErrorMsg.PodOverlapsTimeWindowOfAnotherStopInSameJob
    );
  },

  isArrivalDateTimeOutsideTimeWindowOfOtherStopsInSameJob: (
    arrivalDateTime: string | undefined,
    stops: ManifestStop[],
    jobId: number
  ) => {
    return isDateTimeOutsideTimeWindowOfOtherStopsInSameJob(
      arrivalDateTime,
      stops,
      jobId,
      DateTimeErrorMsg.ArrivalOverlapsTimeWindowOfAnotherStopInSameJob
    );
  },

  isDepartureDateTimeOutsideTimeWindowOfOtherStopsInSameJob: (
    departureDateTime: string | undefined,
    stops: ManifestStop[],
    jobId: number
  ) => {
    return isDateTimeOutsideTimeWindowOfOtherStopsInSameJob(
      departureDateTime,
      stops,
      jobId,
      DateTimeErrorMsg.DepartureOverlapsTimeWindowOfAnotherStopInSameJob
    );
  },
  isIdexxJob: (stop: ManifestStop, idexxRegEx: string) => {
    const regex = new RegExp(idexxRegEx, "i");
    return regex.test(stop.order.customer.name ?? "");
  },

  isFirstIdexxStopInJob: (stops: ManifestStop[], firstJobStopId: number, jobId: number, idexxRegEx: string) => {
    const idexxStops = stops
      .filter((stop) => stop.job.jobId === jobId && CompleteStopIdexxRules.isIdexxJob(stop, idexxRegEx))
      .sort((a, b) => a.manifestSequence - b.manifestSequence);
    if (idexxStops.length === 0) return false;

    return idexxStops[0].jobStopId === firstJobStopId;
  }
};

export default CompleteStopIdexxRules;
